import { useEffect, useState, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  useMap
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getCoords } from "../utils/getCoords";
import cities from "../data/cities";
import TopBar from "../components/TopBar";
import { trackEvent } from "../utils/analytics";
import { supabase } from "../utils/supabase";

/* =========================
   CLOUD STYLES & ANIMATIONS
========================= */
const cloudStyles = `
  @keyframes floatCloudLeft {
    0% { transform: translateX(0px); }
    50% { transform: translateX(15px); }
    100% { transform: translateX(0px); }
  }
  @keyframes floatCloudRight {
    0% { transform: translateX(0px); }
    50% { transform: translateX(-15px); }
    100% { transform: translateX(0px); }
  }

  /* FLUFFY CLOUD BASE */
  .cloud-shape {
    position: absolute;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 50px;
    pointer-events: none;
    filter: drop-shadow(0 6px 12px rgba(255, 255, 255, 0.6));
    z-index: 0;
  }

  /* TOP PUFFS */
  .cloud-shape::before,
  .cloud-shape::after {
    content: '';
    position: absolute;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 50%;
  }

  .cloud-shape::before {
    width: 50%;
    height: 110%;
    top: -45%;
    left: 15%;
  }

  .cloud-shape::after {
    width: 42%;
    height: 90%;
    top: -35%;
    right: 15%;
  }

  /* POSITIONS & SIZES */
  .cloud1 {
    left: -20px;
    top: 80px;
    width: 170px;
    height: 50px;
    animation: floatCloudLeft 8s ease-in-out infinite;
  }

  .cloud2 {
    right: -25px;
    top: 160px;
    width: 190px;
    height: 55px;
    animation: floatCloudRight 9s ease-in-out infinite;
  }

  .cloud3 {
    left: -30px;
    top: 450px;
    width: 160px;
    height: 48px;
    animation: floatCloudLeft 10s ease-in-out infinite;
  }

  .cloud4 {
    right: -30px;
    top: 620px;
    width: 180px;
    height: 52px;
    animation: floatCloudRight 11s ease-in-out infinite;
  }
`;

/* =========================
   DATE HELPER FUNCTIONS
========================= */

// Helper 1: Get YYYY-MM-DD in user's LOCAL timezone
const getTodayLocalDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper 2: Calculate exact integer day difference between two local date strings
const getDaysBetween = (dateStr1, dateStr2) => {
  const d1 = new Date(`${dateStr1}T00:00:00`);
  const d2 = new Date(`${dateStr2}T00:00:00`);
  return Math.round(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24));
};

/* =========================
   ACHIEVEMENT SHEET & EVALUATOR
========================= */

const ACHIEVEMENTS_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1-B3reA5h2nkQOg3RlrHXhtrnJ_TNfzdhQOt26llfi8I/gviz/tq?tqx=out:csv&sheet=Achievements";

function parseCSV(text) {
  const rows = [];
  let row = [];
  let value = "";
  let insideQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"' && insideQuotes && next === '"') {
      value += '"';
      i++;
      continue;
    }
    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }
    if (char === "," && !insideQuotes) {
      row.push(value);
      value = "";
      continue;
    }
    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && next === "\n") i++;
      row.push(value);
      value = "";
      if (row.some((cell) => cell.trim() !== "")) rows.push(row);
      row = [];
      continue;
    }
    value += char;
  }
  if (value !== "" || row.length > 0) {
    row.push(value);
    if (row.some((cell) => cell.trim() !== "")) rows.push(row);
  }
  return rows;
}

function cleanStr(val) {
  return String(val ?? "").trim();
}

function parseNum(val) {
  if (val === null || val === undefined) return 0;
  const cleaned = String(val).replace(/,/g, "").replace(/[^\d.-]/g, "");
  const res = Number(cleaned);
  return Number.isFinite(res) ? res : 0;
}

function calculateLongestStreak(history) {
  if (!history.length) return 0;
  const dates = history
    .filter((e) => e?.success)
    .map((e) => e.date)
    .filter(Boolean)
    .sort();

  if (!dates.length) return 0;
  let longest = 1;
  let current = 1;

  for (let i = 1; i < dates.length; i++) {
    const prev = new Date(dates[i - 1]);
    const curr = new Date(dates[i]);
    const diff = Math.round(Math.abs(curr - prev) / (1000 * 60 * 60 * 24));

    if (diff === 1) {
      current++;
      longest = Math.max(longest, current);
    } else if (diff > 1) {
      current = 1;
    }
  }
  return longest;
}

function evaluateAchievements(historyList, streakVal, sheetRows) {
  const totalGames = historyList.length;
  const longestStreak = calculateLongestStreak(historyList);
  const maxStreak = Math.max(streakVal, longestStreak);

  const hasOneGuessWin = historyList.some(
    (e) => e?.success && Number(e?.guesses) === 1
  );

  const row = sheetRows.find((r) => parseNum(r.streak) === streakVal) || {};
  const totalMiles = parseNum(row["total miles"]);
  const continents = parseNum(row.continents);
  const countries = parseNum(row.countries);
  const hours = parseNum(row.hours);

  const achievements = [
    { id: "first-flight", icon: "🛫", title: "First Flight", unlocked: totalGames >= 1 },
    { id: "frequent-flyer", icon: "🎟️", title: "Frequent Flyer", unlocked: totalGames >= 100 },
    { id: "weekender", icon: "🔥", title: "Weekender", unlocked: maxStreak >= 7 },
    { id: "monthly-flyer", icon: "🗓️", title: "Monthly Flyer", unlocked: maxStreak >= 30 },
    { id: "century-flyer", icon: "💯", title: "Century Flyer", unlocked: maxStreak >= 100 },
    { id: "year-in-air", icon: "🎆", title: "Year in the Air", unlocked: maxStreak >= 365 },
    { id: "around-world", icon: "🔄", title: "Around the World", unlocked: totalMiles >= 24901 },
    { id: "moon", icon: "🌙", title: "To the Moon", unlocked: totalMiles >= 238855 },
    { id: "millionaire", icon: "💸", title: "Millionaire", unlocked: totalMiles >= 1000000 },
    { id: "six-continents", icon: "🌍", title: "Six Continents", unlocked: continents >= 6 },
    { id: "explorer", icon: "🧭", title: "Explorer", unlocked: countries >= 10 },
    { id: "globetrotter", icon: "🗺️", title: "Globetrotter", unlocked: countries >= 25 },
    { id: "world-traveller", icon: "🌐", title: "World Traveller", unlocked: countries >= 50 },
    { id: "all-countries", icon: "👑", title: "Global Master", unlocked: countries >= 188 },
    { id: "around-the-clock", icon: "⏱️", title: "Flight Time", unlocked: hours >= 24 },
    { id: "sky-cruiser", icon: "✈️", title: "Sky Cruiser", unlocked: hours >= 100 },
    { id: "sky-captain", icon: "👨‍✈️", title: "Sky Captain", unlocked: hours >= 500 },
    { id: "lucky-guess", icon: "🍀", title: "Lucky Guess", unlocked: hasOneGuessWin },
  ];

  return achievements.filter((a) => a.unlocked);
}

/* =========================
   FIT MAP TO ROUTE
========================= */

function FitBounds({ start, end }) {
  const map = useMap();

  useEffect(() => {
    if (!start || !end) return;

    const bounds = L.latLngBounds([start, end]);

    map.fitBounds(bounds, {
      padding: [60, 60],
      animate: true
    });
  }, [start, end, map]);

  return null;
}

/* =========================
   CONFETTI
========================= */

function Confetti() {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const pieces = Array.from({ length: 30 }).map(() => {
      const node = document.createElement("div");

      const size = 6 + Math.random() * 6;
      const x = Math.random() * window.innerWidth;
      const y = -Math.random() * (window.innerHeight * 0.5);

      const vx = (Math.random() - 0.5) * 1.5;
      const vy = 2 + Math.random() * 3;
      const vr = (Math.random() - 0.5) * 6;

      node.style.position = "absolute";
      node.style.width = `${size}px`;
      node.style.height = `${size * 1.3}px`;
      node.style.background =
        ["#60a5fa", "#34d399", "#f472b6", "#facc15"][
          Math.floor(Math.random() * 4)
        ];
      node.style.borderRadius = "2px";

      const rot = Math.random() * 360;
      node.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;

      el.appendChild(node);

      return { node, x, y, vx, vy, rot, vr };
    });

    let frame;
    let last = performance.now();

    const animate = (t) => {
      const dt = Math.min((t - last) / 16, 2);
      last = t;

      pieces.forEach((p) => {
        p.vy += 0.1 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;

        p.node.style.transform =
          `translate(${p.x}px, ${p.y}px) rotate(${p.rot}deg)`;
      });

      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div 
      ref={containerRef}
      style={{
      position: "fixed",
      inset: 0,
      pointerEvents: "none",
      overflow: "hidden",
      zIndex: 99999
    }} />
  );
}

/* =========================
   MAIN COMPONENT
========================= */

export default function EndGamePage({ puzzle, gameData, onClose, openHowToPlay, onStats }) {
  const guesses = gameData?.guesses || [];
  const puzzleCityName = puzzle?.name;
  const cityData = useMemo(
    () => cities.find(c => c.name === puzzleCityName),
    [puzzleCityName]
  );
  const flag = cityData?.flag || "";

  const outcome = gameData?.outcome;
  const isWin = outcome === "win";
  const isLose = outcome === "lose";

  const [newAchievements, setNewAchievements] = useState([]);
  const [streak, setStreak] = useState(() => Number(localStorage.getItem("streak") || 0));
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("guessHistory") || "[]");
    } catch (e) {
      return [];
    }
  });

  /* =========================
     APP INITIALIZATION EFFECT
     (Resets broken streaks on app load)
  ========================= */
  useEffect(() => {
    const today = getTodayLocalDate();
    const lastPlayed = localStorage.getItem("lastPlayed");
    let streakValue = Number(localStorage.getItem("streak") || 0);

    if (lastPlayed) {
      const diffDays = getDaysBetween(lastPlayed, today);
      if (diffDays > 1) {
        streakValue = 0;
        localStorage.setItem("streak", "0");
      }
    }

    setStreak(streakValue);
  }, []);

  /* =========================
     SINGLE GAME PROCESSING EFFECT
  ========================= */
  useEffect(() => {
    if (!gameData || (!isWin && !isLose)) return;

    async function processGameAndAchievements() {
      const today = getTodayLocalDate();
      let streakValue = Number(localStorage.getItem("streak") || 0);
      let currentHistory = [];

      try {
        currentHistory = JSON.parse(localStorage.getItem("guessHistory") || "[]");
      } catch (e) {
        currentHistory = [];
      }

      const alreadyProcessedToday = currentHistory.some(entry => entry.date === today);
      if (alreadyProcessedToday) {
        setStreak(streakValue);
        setHistory(currentHistory);
        return;
      }

      // Check missed days first to establish correct baseline streak
      const lastPlayed = localStorage.getItem("lastPlayed");
      if (lastPlayed) {
        const diffDays = getDaysBetween(lastPlayed, today);
        if (diffDays > 1) {
          streakValue = 0;
        }
      }

      // 1. Fetch Google Sheet data for travel calculations
      let sheetRows = [];
      try {
        const res = await fetch(`${ACHIEVEMENTS_SHEET_URL}&t=${Date.now()}`, { cache: "no-store" });
        if (res.ok) {
          const text = await res.text();
          const parsed = parseCSV(text);
          if (parsed.length) {
            const headers = parsed[0].map((h) => cleanStr(h).toLowerCase());
            sheetRows = parsed.slice(1).map((row) => {
              const obj = {};
              headers.forEach((h, i) => { obj[h] = cleanStr(row[i]); });
              return obj;
            });
          }
        }
      } catch (err) {
        console.error("Could not fetch sheet for achievements:", err);
      }

      // 2. Unlocked achievements BEFORE this game
      const preAchievements = evaluateAchievements(currentHistory, streakValue, sheetRows);

      // 3. Update streak & history based on win/loss
      if (isLose) {
        streakValue = 0;
      } else if (isWin) {
        streakValue += 1;
      }

      const updatedHistory = [
        ...currentHistory,
        { date: today, guesses: guesses.length, success: isWin },
      ];

      // 4. Unlocked achievements AFTER this game
      const postAchievements = evaluateAchievements(updatedHistory, streakValue, sheetRows);
      const freshlyUnlocked = postAchievements.filter(
        (post) => !preAchievements.some((pre) => pre.id === post.id)
      );

      if (freshlyUnlocked.length > 0) {
        setNewAchievements(freshlyUnlocked);
      }

      // 5. Save state
      localStorage.setItem("streak", String(streakValue));
      localStorage.setItem("lastPlayed", today);
      localStorage.setItem("guessHistory", JSON.stringify(updatedHistory));

      setStreak(streakValue);
      setHistory(updatedHistory);

      // 6. Supabase record (Untouched)
      const now = new Date();
      const localToday = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0];

      const { error } = await supabase.rpc('record_completion', { 
        p_guesses: guesses.length,
        p_date: localToday,
        p_success: isWin
      });
      
      if (error) console.error("Supabase error:", error.message);
    }

    processGameAndAchievements();
  }, [isWin, isLose, gameData, guesses.length]);

  const departureCityData = useMemo(
    () => cities.find(c => c.name === puzzle?.departure),
    [puzzle?.departure]
  );
  const departureFlag = departureCityData?.flag || "";

  const startObj = useMemo(() => getCoords(puzzle?.departure), [puzzle]);
  const endObj = useMemo(() => getCoords(puzzleCityName), [puzzleCityName]);

  const start = useMemo(() => startObj ? [startObj.lat, startObj.lng] : null, [startObj]);
  const end = useMemo(() => endObj ? [endObj.lat, endObj.lng] : null, [endObj]);

  const [progress, setProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");

  /* =========================
     COUNTDOWN
  ========================= */
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 0, 0);

      const diff = nextMidnight - now;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff / (1000 * 60)) % 60);

      setTimeLeft(`${hours}h ${mins}m`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, []);

  /* =========================
     CONFETTI TRIGGER
  ========================= */
  useEffect(() => {
    setShowConfetti(true);
  }, []);

  /* =========================
     PATH
  ========================= */
  const path = useMemo(() => {
    if (!start || !end) return [];

    const pts = [];
    for (let i = 0; i <= 60 * progress; i++) {
      const t = i / 60;
      pts.push([
        start[0] + (end[0] - start[0]) * t,
        start[1] + (end[1] - start[1]) * t
      ]);
    }
    return pts;
  }, [progress, start, end]);

  /* =========================
     ANIMATION
  ========================= */
  useEffect(() => {
    let startTime = performance.now();
    const duration = 2500;

    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      setProgress(t);
      if (t < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, []);

  /* =========================
     WORDLE DISTRIBUTION
  ========================= */
  const distribution = useMemo(() => {
    const counts = [0, 0, 0, 0, 0, 0];

    if (history && Array.isArray(history)) {
      history.forEach(g => {
        if (g && g.guesses) {
          const idx = Math.min(Math.max(g.guesses - 1, 0), 5);
          counts[idx]++;
        }
      });
    }

    return counts;
  }, [history]);

  const maxDist = Math.max(...distribution, 1);

  /* =========================
     SHARE HANDLERS
  ========================= */
  const getSquare = (status) => {
    if (status === "correct") return "🟩";
    if (status === "bordering") return "🟨";
    return "⬜";
  };

  const handleShare = async () => {
    trackEvent("share_clicked", {
      guesses_used: guesses.length,
      outcome: outcome
    });
    const emojiGrid = guesses
      .map(g =>
        `${getSquare(g.continentStatus)}${getSquare(g.countryStatus)}${getSquare(g.cityStatus)}`
      )
      .join("\n");

    const text = `${isWin
      ? `I departed ${puzzle.departure} and solved today's Flightle in ${guesses.length}${guesses.length === 1 ? ' guess' : ' guesses'}!`
      : `I departed ${puzzle.departure}, but couldn't solve today's Flightle in 6 guesses!`}

${emojiGrid}

🔥 ${streak} day streak
Play Flightle at https://playflightle.com?ref=share`;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy to clipboard:", err);
      }
    };

    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: "My Flightle result",
          text
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      await copyToClipboard();
    }
  };

  const handleShareAchievement = async (achievement) => {
    trackEvent("share_achievement_clicked", {
      achievement_id: achievement.id,
      achievement_title: achievement.title
    });

    const text = `I departed ${puzzle?.departure || "a city"} and got the ${achievement.title} ${achievement.icon} achievement!\n\nPlay Flightle at https://playflightle.com?ref=share`;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy to clipboard:", err);
      }
    };

    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: "Flightle Achievement Unlocked!",
          text
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      await copyToClipboard();
    }
  };

  const pinIcon = (label, colour = "#60a5fa", textColour = "#111827") =>
    L.divIcon({
      className: "",
      html: `
        <div style="
          display:flex;
          flex-direction:column;
          align-items:center;
          transform:translateY(-6px);
        ">
          <div style="
            background:white;
            padding:3px 6px;
            border-radius:7px;
            font-size:11px;
            font-weight:600;
            white-space:nowrap;
            color:${textColour};
            box-shadow:0 2px 6px rgba(0,0,0,0.12);
            margin-bottom:3px;
          ">
            ${label}
          </div>
          <div style="
            width:16px;
            height:16px;
            background:${colour};
            border-radius:50% 50% 50% 0;
            transform:rotate(-45deg);
            display:flex;
            align-items:center;
            justify-content:center;
            border:2px solid white;
            box-shadow:0 2px 6px rgba(0,0,0,0.25);
          ">
          </div>
        </div>
      `,
      iconAnchor: [8, 42]
    });

  if (!start || !end) return <div>Loading...</div>;

  return (
    <div style={styles.overlay}>
      <style>{cloudStyles}</style>

      {/* CLOUDS VISIBLE IN BACKGROUND */}
      <div className="cloud-shape cloud1"></div>
      <div className="cloud-shape cloud2"></div>
      <div className="cloud-shape cloud3"></div>
      <div className="cloud-shape cloud4"></div>

      <TopBar onBack={onClose} onHelp={openHowToPlay} onStats={onStats}/>
      {showConfetti && <Confetti />}

      <div style={styles.mainContainer}>

        {/* --- HERO SECTION --- */}
        <div style={styles.heroSection}>
          <div>
            <h1 style={{
              ...styles.correctTitle,
              color: isLose ? "#dc2626" : "#16a34a"
            }}>
              <div style={{ 
                background: isLose ? "#dc2626" : "#16a34a",
                color: "white", 
                borderRadius: "50%", 
                width: 36, 
                height: 36, 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                fontSize: 20 
              }}>
                {isLose ? "✗" : "✓"}
              </div>
              {isLose ? "Unlucky!" : "Correct!"}
            </h1>
            <div style={{ fontSize: 16, color: "#64748b" }}>
              {isLose ? "The destination was actually" : "The destination was"}
            </div>
            <div style={{ fontSize: 28, fontWeight: "bold", color: "#1e293b" }}>
              {puzzleCityName} {flag}
            </div>
          </div>

          <div style={{ ...styles.card, padding: "16px 24px", textAlign: "center", border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 14, fontWeight: "bold", color: "#1e293b" }}>
              {isLose ? "You used all" : "You got it in"}
            </div>
            <div style={{ 
              fontSize: 48, 
              fontWeight: "900", 
              color: isLose ? "#dc2626" : "#16a34a",
              lineHeight: 1 
            }}>
              {guesses.length}
            </div>
            <div style={{ fontSize: 14, fontWeight: "bold", color: "#1e293b" }}>
              {guesses.length === 1 ? "guess!" : "guesses!"}
            </div>
          </div>
        </div>

        {/* --- STREAK & TIMER --- */}
        <div style={styles.topGrid}>
          <div style={{ ...styles.card, padding:14, display: "flex", alignItems: "center", gap: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 32 }}>🔥</div>
            <div>
              <div style={{ fontSize: 24, fontWeight: "bold", color: "#ea580c" }}>{streak}</div>
              <div style={{ fontSize: 14, fontWeight: "bold", color: "#64748b" }}>Current Streak</div>
            </div>
          </div>

          <div style={{ ...styles.card, padding:14, display: "flex", alignItems: "center", gap: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 24, color: "#64748b" }}>📅</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: "bold", color: "#64748b" }}>Next puzzle:</div>
              <div style={{ fontSize: 18, fontWeight: "bold", margin: "3px 0" }}>{timeLeft}</div>
              <div style={{ fontSize: 14, fontWeight: "bold", color: "#64748b" }}>Come back tomorrow!</div>
            </div>
          </div>
        </div>

        {/* --- MAP --- */}
        <div style={{ ...styles.section, padding: 0 }}>
          <div style={styles.mapWrapper}>
            <MapContainer center={start} zoom={2} style={styles.map} scrollWheelZoom={false}
              dragging={false} doubleClickZoom={false} touchZoom={false} zoomControl={false}>
              <FitBounds start={start} end={end} />
              <TileLayer
                attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=9owPkzMhrfRqHhkr8Rhw"
              />
              <Polyline
                positions={path}
                color="#16a34a"
                dashArray="6 10"
              />

              <Marker
                position={start}
                icon={pinIcon(`Departed: ${puzzle.departure} ${departureFlag}`,"#60a5fa","#6b7280")}
              />

              <Marker
                position={end}
                icon={pinIcon(`Landed: ${puzzleCityName} ${flag}`, "#16a34a")}
              />
            </MapContainer>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ 
            ...styles.section, 
            flex: 1, 
            display: "grid", 
            placeItems: "center", 
            padding: "16px 8px"
          }}>
            <div style={{ 
              fontSize: 18, 
              fontWeight: "bold",
              textAlign: "center",
              lineHeight: "1.2",
              color: isLose ? "#dc2626" : "#16a34a"
            }}>
              {isLose ? "Try again tomorrow!" : "You did it!"}
            </div>
          </div>

          <div style={{ ...styles.section, flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 15, opacity: 0.6, fontWeight: "bold" }}>Total guesses</div>
            <div style={{ fontSize: 16, fontWeight: "bold" }}>
              {guesses.length}
            </div>
          </div>

          <div style={{ 
            ...styles.section, 
            flex: 1, 
            display: "grid", 
            placeItems: "center", 
            padding: "16px 8px"
          }}>
            <div style={{ 
              fontWeight: "bold", 
              textAlign: "center",
              lineHeight: "1.3",
              fontSize: 18,
              color: guesses.length <= 3 ? "#16a34a" : guesses.length <= 5 ? "#eab60c" : "#dc2626"
            }}>
              {guesses.length <= 3 && "Perfect landing!"}
              {(guesses.length === 4 || guesses.length === 5) && "Some turbulence but nice landing!"}
              {guesses.length >= 6 && "Crash landing!"}
            </div>
          </div>
        </div>

        {/* --- GUESS DISTRIBUTION & TRIVIA --- */}
        <div style={styles.splitRow}>
          <div style={{ ...styles.column, ...styles.card, flex: 1.2 }}>
            <h3 style={{ fontSize: 16, marginBottom: 12, color: "#1e3a8a" }}>Your Guess Distribution</h3>
            {distribution.map((count, i) => {
              const isCurrentScore = (i + 1) === guesses.length;
              return (
                <div key={i} style={styles.barContainer}>
                  <div style={styles.barNumber}>{i + 1}</div>
                  <div style={styles.barTrack}>
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.max((count / maxDist) * 100, 5)}%`,
                        background: isCurrentScore ? "#16a34a" : "#94a3b8",
                        borderRadius: 4,
                        transition: "width 1s ease-out"
                      }}
                    />
                    <span style={{ 
                      position: "absolute", 
                      right: 6, 
                      top: 1, 
                      fontSize: 11, 
                      fontWeight: "bold", 
                      color: count > 0 ? "white" : "#64748b" 
                    }}>
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ ...styles.column, ...styles.card }}>
            <h3 style={{ fontSize: 16, marginBottom: 12, color: "#1e3a8a" }}>Did you know?</h3>
            <img 
              src={`/city-images/${puzzleCityName}.jpg`}
              alt={puzzleCityName} 
              style={styles.cityImage} 
            />
            <div style={styles.factText}>
              <strong>{puzzleCityName}</strong> is {
                puzzle.clues[0].charAt(0).toLowerCase() + puzzle.clues[0].slice(1)
              }
            </div>
          </div>
        </div>

        <button style={styles.bigShareBtn} onClick={handleShare}>
          Share your result
        </button>

        {/* --- SUPPORT SECTION --- */}
        <div style={{
          ...styles.section,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{ fontWeight: "600", color: "#1e293b" }}>Enjoying Flightle? ✈️</div>
          <a 
            href="https://ko-fi.com/flightlematt"
            target="_blank"
            rel="noreferrer"
            style={styles.supportLink}
          >
            Support the journey
          </a>
        </div>

        {/* --- FOOTER LINKS --- */}
        <div style={{ flexGrow: 1 }} />

        <div style={{
          textAlign: "center",
          paddingBottom: 10,
          fontSize: 13
        }}>
          <a
            href="https://forms.gle/J74Yfj8Wa8fhuyHp8"
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#64748b",
              textDecoration: "underline",
              marginRight: 16
            }}
          >
            Give feedback
          </a>

          <a
            href="/privacy.html"
            style={{
              color: "#64748b",
              textDecoration: "underline"
            }}
          >
            Privacy policy
          </a>
        </div>
      </div>

      {/* =========================
         ACHIEVEMENT UNLOCKED POPUP
      ========================= */}
      {newAchievements.length > 0 && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <div style={styles.popupBadge}>NEW ACHIEVEMENT!</div>
            <div style={styles.popupIcon}>{newAchievements[0].icon}</div>
            <div style={styles.popupTitle}>{newAchievements[0].title}</div>
            
            {/* SHARE ACHIEVEMENT BUTTON */}
            <button
              style={styles.shareAchievementBtn}
              onClick={() => handleShareAchievement(newAchievements[0])}
            >
              Share Achievement 📤
            </button>

            <div style={{ display: "flex", gap: 8, marginTop: 12, width: "100%" }}>
              <button
                style={{ ...styles.popupBtn, background: "#0284c7" }}
                onClick={() => {
                  setNewAchievements([]);
                  if (onStats) onStats();
                }}
              >
                View Flight Log ✈️
              </button>

              {newAchievements.length > 1 ? (
                <button
                  style={styles.popupBtn}
                  onClick={() => setNewAchievements((prev) => prev.slice(1))}
                >
                  Next →
                </button>
              ) : (
                <button
                  style={{ ...styles.popupBtn, background: "#f1f5f9", color: "#334155" }}
                  onClick={() => setNewAchievements([])}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* =========================
   STYLES
========================= */

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: `linear-gradient(to bottom, #deecfc 0%, #dff3ff 45%, #eef7ff 100%)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "60px 16px 60px",
    overflowY: "auto",
    overflowX: "hidden",
    fontFamily: "'Inter', sans-serif",
    color: "#1e293b"
  },
  mainContainer: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 600,
    minHeight: "calc(100vh - 120px)",
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  card: {
    background: "rgba(255, 255, 255, 0.94)",
    backdropFilter: "blur(10px)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
  },
  heroSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
  },
  correctTitle: {
    color: "#16a34a",
    fontSize: 36,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "0 0 8px 0"
  },
  topGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 8
  },
  section: {
    background: "rgba(255, 255, 255, 0.94)",
    backdropFilter: "blur(10px)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 20px rgba(0,0,0,0.05)"
  },
  mapWrapper: {
    height: 300,
    width: "100%",
    borderRadius: 16,
    overflow: "hidden"
  },
  map: { height: "100%", width: "100%" },
  splitRow: {
    display: "flex",
    gap: 12
  },
  column: {
    flex: 1
  },
  barContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6
  },
  barNumber: {
    fontSize: 12,
    fontWeight: "bold",
    width: 12
  },
  barTrack: {
    flex: 1,
    height: 18,
    background: "#f1f5f9",
    borderRadius: 4,
    position: "relative",
    overflow: "hidden"
  },
  cityImage: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    borderRadius: 8,
    marginBottom: 8
  },
  factText: {
    fontSize: 13,
    lineHeight: 1.4,
    color: "#334155"
  },
  bigShareBtn: {
    marginTop: 10,
    padding: "14px",
    borderRadius: 12,
    background: "#16a34a",
    color: "white",
    border: "none",
    fontSize: 16,
    fontWeight: "bold",
    cursor: "pointer",
    width: "100%"
  },
  supportLink: {
    color: "#0284c7",
    fontWeight: "bold",
    textDecoration: "none",
    fontSize: 14
  },
  /* Popup Styles */
  popupOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.65)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100000,
    padding: 16
  },
  popupCard: {
    background: "white",
    borderRadius: 20,
    padding: "28px 24px 20px",
    textAlign: "center",
    maxWidth: 340,
    width: "100%",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  popupBadge: {
    background: "#e0f2fe",
    color: "#0284c7",
    fontSize: 12,
    fontWeight: "800",
    letterSpacing: "0.05em",
    padding: "4px 12px",
    borderRadius: 20,
    marginBottom: 12,
    textTransform: "uppercase"
  },
  popupIcon: {
    fontSize: 56,
    lineHeight: 1,
    margin: "8px 0 12px"
  },
  popupTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#0f172a"
  },
  popupBtn: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: "#0284c7",
    color: "white",
    fontWeight: "bold",
    fontSize: 14,
    cursor: "pointer"
  }
};